import { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import api from '../api';

function SummaryCard({ title, value, body }) {
  return (
    <div className="rounded-3xl border border-[#f5b221]/16 bg-[linear-gradient(180deg,#161616,#090909)] p-5">
      <p className="text-sm uppercase text-gray-400">{title}</p>
      <p className="mt-3 text-4xl font-bold text-[#f5b221]">{value}</p>
      <p className="mt-2 text-sm text-gray-500">{body}</p>
    </div>
  );
}

export default function SecurityOperations() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [workingKey, setWorkingKey] = useState('');
  const [summary, setSummary] = useState({});
  const [riskyUsers, setRiskyUsers] = useState([]);
  const [recentSessions, setRecentSessions] = useState([]);

  async function load() {
    setLoading(true);
    setError('');
    try {
      const response = await api.get('/admin/platform/security-operations');
      setSummary(response.data.summary || {});
      setRiskyUsers(response.data.riskyUsers || []);
      setRecentSessions(response.data.recentSessions || []);
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not load security operations');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function revokeSession(userId, sessionId) {
    const reason = window.prompt('Reason for revoking this session:', 'Revoked from admin security operations');
    if (reason === null) return;
    setWorkingKey(`${userId}:${sessionId}`);
    try {
      await api.post(`/admin/platform/security-operations/${userId}/sessions/${sessionId}/revoke`, { reason });
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not revoke that session');
    } finally {
      setWorkingKey('');
    }
  }

  return (
    <Layout>
      <div className="space-y-8">
        <section className="rounded-3xl border border-[#f5b221]/18 bg-[radial-gradient(circle_at_top_left,rgba(245,178,33,0.16),transparent_34%),linear-gradient(180deg,#171717,#090909)] p-6">
          <p className="text-sm uppercase tracking-[0.2em] text-[#f7d88a]">Security Ops</p>
          <h2 className="mt-3 text-3xl font-semibold text-white">Session hygiene and account hardening</h2>
          <p className="mt-2 max-w-3xl text-sm text-gray-400">
            This page keeps risky sessions, PIN adoption, and device cleanup on the web admin side, separate from everyday user and support workflows.
          </p>
        </section>

        {error ? <p className="text-sm text-red-400">{error}</p> : null}

        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4">
          <SummaryCard title="Active sessions" value={summary.activeSessions || 0} body="Across sampled user accounts" />
          <SummaryCard title="Revoked in 7d" value={summary.revokedSessionsLast7d || 0} body="Recent security cleanup actions" />
          <SummaryCard title="Verified without PIN" value={summary.verifiedWithoutPin || 0} body="Accounts that should be nudged harder" />
          <SummaryCard title="Open privacy requests" value={summary.openPrivacyRequests || 0} body="Trust workload still waiting" />
        </div>

        <section className="rounded-3xl border border-gray-800 bg-gray-950 p-6">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-semibold text-white">Risky accounts</h3>
            <button
              type="button"
              onClick={load}
              className="rounded-full border border-[#f5b221]/40 px-4 py-2 text-sm font-semibold text-[#f7d88a] transition hover:bg-[#f5b221]/10"
            >
              Refresh
            </button>
          </div>

          {loading ? (
            <p className="mt-6 text-sm text-gray-400">Loading security signals...</p>
          ) : (
            <div className="mt-6 space-y-4">
              {riskyUsers.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-gray-700 p-5 text-sm text-gray-400">
                  No risky user signals were found in the sampled window.
                </div>
              ) : (
                riskyUsers.map((user) => (
                  <div key={user.id} className="rounded-2xl border border-gray-800 bg-black/60 p-5">
                    <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                      <div>
                        <h4 className="text-lg font-semibold text-white">{user.name}</h4>
                        <p className="text-sm text-gray-400">
                          {user.email || 'No email'} · {user.shimbirrId || 'No Shimbirr ID'} · {user.verificationStage}
                        </p>
                      </div>
                      <span className="rounded-full bg-[#f5b221]/15 px-3 py-1 text-xs uppercase text-[#f7d88a]">
                        {user.activeSessions} active sessions
                      </span>
                    </div>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {(user.reasons || []).map((reason) => (
                        <span key={reason} className="rounded-full bg-red-500/12 px-3 py-2 text-xs uppercase text-red-200">
                          {reason}
                        </span>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </section>

        <section className="rounded-3xl border border-gray-800 bg-gray-950 p-6">
          <h3 className="text-xl font-semibold text-white">Recent sessions</h3>
          <div className="mt-6 space-y-4">
            {recentSessions.map((session) => (
              <div key={`${session.userId}:${session.sessionId}`} className="rounded-2xl border border-gray-800 bg-black/60 p-5">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                  <div>
                    <h4 className="text-lg font-semibold text-white">{session.userLabel}</h4>
                    <p className="text-sm text-gray-400">
                      {session.deviceLabel} · {session.platform || 'unknown'} · {session.source || 'mobile'}
                      {session.appVersion ? ` · v${session.appVersion}` : ''}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <span className="rounded-full bg-gray-800 px-3 py-1 text-xs uppercase text-gray-200">
                      {session.ipAddress || 'No IP'}
                    </span>
                    <button
                      type="button"
                      onClick={() => revokeSession(session.userId, session.sessionId)}
                      disabled={workingKey === `${session.userId}:${session.sessionId}`}
                      className="rounded-full border border-red-500/50 px-4 py-2 text-sm font-semibold text-red-200 transition hover:bg-red-500/10 disabled:opacity-60"
                    >
                      {workingKey === `${session.userId}:${session.sessionId}` ? 'Working...' : 'Revoke'}
                    </button>
                  </div>
                </div>
                <p className="mt-3 text-sm text-gray-400">
                  Seen {session.lastSeenAt ? new Date(session.lastSeenAt).toLocaleString() : 'Unknown'}
                </p>
              </div>
            ))}
          </div>
        </section>
      </div>
    </Layout>
  );
}
